import { 
  ref, 
  set, 
  get, 
  onValue, 
  off, 
  push, 
  update, 
  remove,
  serverTimestamp,
  onDisconnect
} from 'firebase/database';
import { database, auth } from './firebase';
import { signInAnonymously, User } from 'firebase/auth';
import { GameState, FactionType, CampaignType } from './types';
import { debug } from './debug';
import { AntiCheatValidator } from './gameValidation';
import { validateMoveData } from './security';

export interface GameRoom {
  id: string;
  name: string;
  campaign: CampaignType;
  host: PlayerInfo;
  guest?: PlayerInfo;
  status: 'waiting' | 'ready' | 'playing' | 'finished';
  currentTurn: FactionType;
  turnNumber: number;
  gameState?: any; // Serialized game state
  createdAt: number;
  updatedAt: number;
  lastMoveAt?: number;
  winner?: FactionType;
}

export interface PlayerInfo {
  uid: string;
  name: string;
  faction: FactionType;
  connected: boolean;
  lastSeen: number;
}

export interface GameMove {
  type: 'move' | 'attack' | 'build' | 'endTurn' | 'surrender';
  playerId: string;
  faction: FactionType;
  data: any;
  timestamp: number;
}

export interface NotificationData {
  type: string;
  faction: string;
  targetFaction?: string;
  data: any;
  timestamp: number;
}

export class MultiplayerService {
  private currentUser: User | null = null;
  private currentRoom: GameRoom | null = null;
  private roomRef: any = null;
  private movesRef: any = null;
  private presenceRef: any = null;
  private listeners: Map<string, (data: any) => void> = new Map();
  private reconnectTimer: any = null;
  private antiCheatValidator: AntiCheatValidator = new AntiCheatValidator();

  constructor() {
    this.initAuth();
  }

  private async initAuth() {
    try {
      debug.log('Attempting Firebase authentication...');
      // Sign in anonymously for now (you can add proper auth later)
      const userCredential = await signInAnonymously(auth);
      this.currentUser = userCredential.user;
      debug.log('✅ Signed in anonymously:', this.currentUser.uid);
      this.setupPresence();
    } catch (error: any) {
      debug.error('❌ Failed to sign in:', error);
      debug.error('Error code:', error.code);
      debug.error('Error message:', error.message);
      
      // Check different error types
      if (error.code === 'auth/network-request-failed') {
        debug.warn('⚠️ Network issue detected. Please check your internet connection.');
        debug.warn('You may be offline or Firebase may be blocked by firewall/proxy.');
      } else if (error.code === 'auth/too-many-requests') {
        debug.warn('⚠️ Too many requests. Please wait a moment and try again.');
      } else if (error.code === 'auth/configuration-not-found' || error.code === 'auth/invalid-api-key') {
        debug.warn('⚠️ Firebase configuration issue. Check your .env file.');
      }
      
      // Create a mock user for offline development
      this.currentUser = {
        uid: 'offline_' + Math.random().toString(36).substr(2, 9)
      } as User;
      // Note: Running in offline mode
      debug.warn('📵 Running in offline mode. Multiplayer features disabled.');
    }
  }

  private setupPresence() {
    if (!this.currentUser) return;

    // Set up presence system
    this.presenceRef = ref(database, `.info/connected`);
    onValue(this.presenceRef, (snapshot) => {
      if (snapshot.val() === true && this.currentRoom) {
        // We're connected (or reconnected)
        this.updatePlayerPresence(true);
        
        // Set up disconnect handler
        const playerRef = ref(database, `rooms/${this.currentRoom.id}/${this.isHost() ? 'host' : 'guest'}/connected`);
        onDisconnect(playerRef).set(false);
        onDisconnect(ref(database, `rooms/${this.currentRoom.id}/${this.isHost() ? 'host' : 'guest'}/lastSeen`))
          .set(serverTimestamp());
      }
    });
  }

  private updatePlayerPresence(connected: boolean) {
    if (!this.currentRoom || !this.currentUser) return;

    const playerPath = this.isHost() ? 'host' : 'guest';
    const updates: any = {};
    updates[`${playerPath}/connected`] = connected;
    updates[`${playerPath}/lastSeen`] = serverTimestamp();
    
    if (this.roomRef) {
      update(this.roomRef, updates);
    }
  }

  public async createRoom(name: string, campaign: CampaignType, hostFaction: FactionType): Promise<GameRoom> {
    if (!this.currentUser) {
      throw new Error('Not authenticated');
    }

    const roomsRef = ref(database, 'rooms');
    const newRoomRef = push(roomsRef);
    
    const room: GameRoom = {
      id: newRoomRef.key!,
      name,
      campaign,
      host: {
        uid: this.currentUser.uid,
        name: `Player ${this.currentUser.uid.slice(-4)}`,
        faction: hostFaction,
        connected: true,
        lastSeen: Date.now()
      },
      status: 'waiting',
      currentTurn: hostFaction,
      turnNumber: 1,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    await set(newRoomRef, room);
    this.currentRoom = room;
    this.roomRef = newRoomRef;
    this.setupRoomListeners();

    return room;
  }

  public async reconnectToRoom(roomId: string, playerFaction: FactionType): Promise<GameRoom> {
    if (!this.currentUser) {
      throw new Error('Not authenticated');
    }

    const roomRef = ref(database, `rooms/${roomId}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const room = snapshot.val() as GameRoom;
    
    // Determine if we're the host or guest based on faction
    const isHost = room.host.faction === playerFaction;
    const isGuest = room.guest && room.guest.faction === playerFaction;
    
    if (!isHost && !isGuest) {
      throw new Error('Player not in this room');
    }

    // Set up the room connection
    this.currentRoom = room;
    this.roomRef = roomRef;
    this.setupRoomListeners();
    
    // Update presence for reconnection
    if (isHost) {
      await update(ref(database, `rooms/${roomId}/host`), {
        ...room.host,
        connected: true
      });
    } else if (isGuest) {
      await update(ref(database, `rooms/${roomId}/guest`), {
        ...room.guest,
        connected: true
      });
    }

    return this.currentRoom;
  }

  public async joinRoom(roomId: string, guestFaction: FactionType): Promise<GameRoom> {
    if (!this.currentUser) {
      throw new Error('Not authenticated');
    }

    const roomRef = ref(database, `rooms/${roomId}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const room = snapshot.val() as GameRoom;
    
    if (room.guest) {
      throw new Error('Room is full');
    }

    if (room.host.faction === guestFaction) {
      throw new Error('Faction already taken');
    }

    const guest: PlayerInfo = {
      uid: this.currentUser.uid,
      name: `Player ${this.currentUser.uid.slice(-4)}`,
      faction: guestFaction,
      connected: true,
      lastSeen: Date.now()
    };

    const updates: any = {};
    updates['guest'] = guest;
    updates['status'] = 'ready';
    updates['updatedAt'] = serverTimestamp();

    debug.log('Attempting to join room with updates:', {
      roomId,
      path: roomRef.toString(),
      updates,
      currentUser: this.currentUser?.uid
    });

    try {
      await update(roomRef, updates);
      debug.log('Successfully joined room');
    } catch (error) {
      debug.error('Failed to update room:', error);
      throw error;
    }

    this.currentRoom = { ...room, guest, status: 'ready' };
    this.roomRef = roomRef;
    this.setupRoomListeners();

    return this.currentRoom;
  }

  public async listRooms(): Promise<GameRoom[]> {
    const roomsRef = ref(database, 'rooms');
    const snapshot = await get(roomsRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    const rooms: GameRoom[] = [];
    snapshot.forEach((childSnapshot) => {
      const room = childSnapshot.val() as GameRoom;
      // Only show waiting or ready rooms created in last hour
      if ((room.status === 'waiting' || room.status === 'ready') && 
          Date.now() - room.createdAt < 3600000) {
        rooms.push(room);
      }
    });

    return rooms.sort((a, b) => b.createdAt - a.createdAt);
  }

  public async startGame(initialGameState: any): Promise<void> {
    if (!this.currentRoom || !this.isHost()) {
      throw new Error('Only host can start the game');
    }

    if (this.currentRoom.status !== 'ready') {
      throw new Error('Room not ready');
    }

    const updates: any = {};
    updates['status'] = 'playing';
    updates['gameState'] = this.serializeGameState(initialGameState);
    updates['updatedAt'] = serverTimestamp();

    await update(this.roomRef!, updates);
  }

  public async submitMove(move: GameMove): Promise<void> {
    if (!this.currentRoom || !this.currentUser) {
      throw new Error('Not in a room');
    }

    if (this.currentRoom.status !== 'playing') {
      throw new Error('Game not in progress');
    }

    // Basic move data validation
    if (!validateMoveData(move)) {
      throw new Error('Invalid move data structure');
    }

    // Verify it's the player's turn
    const isMyTurn = (this.isHost() && this.currentRoom.currentTurn === this.currentRoom.host.faction) ||
                     (!this.isHost() && this.currentRoom.guest && this.currentRoom.currentTurn === this.currentRoom.guest.faction);
    
    if (!isMyTurn) {
      throw new Error('Not your turn');
    }

    // Anti-cheat validation
    const cheatCheck = this.antiCheatValidator.checkForCheating(
      this.currentUser.uid,
      move
    );
    
    if (!cheatCheck.valid) {
      debug.warn('Potential cheating detected:', cheatCheck.reason);
      throw new Error('Invalid action: ' + cheatCheck.reason);
    }

    // Save move to moves history
    const movesRef = ref(database, `moves/${this.currentRoom.id}`);
    await push(movesRef, move);

    // Update room with latest move timestamp
    const updates: any = {};
    updates['lastMoveAt'] = serverTimestamp();
    await update(this.roomRef!, updates);
  }

  public async updateGameState(gameState: any, nextTurn: FactionType, turnNumber: number): Promise<void> {
    if (!this.currentRoom) {
      throw new Error('Not in a room');
    }

    const updates: any = {};
    updates['gameState'] = this.serializeGameState(gameState);
    updates['currentTurn'] = nextTurn;
    updates['turnNumber'] = turnNumber;
    updates['updatedAt'] = serverTimestamp();

    await update(this.roomRef!, updates);
  }

  public async endGame(winner: FactionType): Promise<void> {
    if (!this.currentRoom) {
      throw new Error('Not in a room');
    }

    const updates: any = {};
    updates['status'] = 'finished';
    updates['winner'] = winner;
    updates['updatedAt'] = serverTimestamp();

    await update(this.roomRef!, updates);
  }

  public async surrenderGame(): Promise<void> {
    if (!this.currentRoom || !this.currentUser) {
      throw new Error('Not in a room');
    }

    // Determine the winner (the opponent)
    const isHost = this.isHost();
    const winner = isHost ? this.currentRoom.guest!.faction : this.currentRoom.host.faction;
    
    // Mark game as finished with opponent as winner
    const updates: any = {};
    updates['status'] = 'finished';
    updates['winner'] = winner;
    updates['surrendered'] = isHost ? 'host' : 'guest';
    updates['updatedAt'] = serverTimestamp();

    await update(this.roomRef!, updates);
    
    debug.log(`Player surrendered. Winner: ${winner}`);
  }

  public onRoomUpdate(callback: (room: GameRoom) => void): void {
    this.listeners.set('room', callback);
  }

  public onMoveReceived(callback: (move: GameMove) => void): void {
    this.listeners.set('move', callback);
  }

  public onGameStateUpdate(callback: (gameState: any) => void): void {
    this.listeners.set('gameState', callback);
  }

  public onPlayerDisconnect(callback: (playerId: string) => void): void {
    this.listeners.set('disconnect', callback);
  }

  private setupRoomListeners() {
    if (!this.roomRef) return;

    // Listen for room updates
    onValue(this.roomRef, (snapshot) => {
      if (snapshot.exists()) {
        const room = snapshot.val() as GameRoom;
        const previousRoom = this.currentRoom;
        this.currentRoom = room;

        // Notify room update
        const roomCallback = this.listeners.get('room');
        if (roomCallback) {
          roomCallback(room);
        }

        // Check for game state updates
        if (room.gameState && (!previousRoom || room.gameState !== previousRoom.gameState)) {
          const gameStateCallback = this.listeners.get('gameState');
          if (gameStateCallback) {
            gameStateCallback(this.deserializeGameState(room.gameState));
          }
        }

        // Check for disconnections
        if (previousRoom) {
          if (previousRoom.host.connected && !room.host.connected) {
            const disconnectCallback = this.listeners.get('disconnect');
            if (disconnectCallback) {
              disconnectCallback(room.host.uid);
            }
          }
          if (previousRoom.guest?.connected && room.guest && !room.guest.connected) {
            const disconnectCallback = this.listeners.get('disconnect');
            if (disconnectCallback) {
              disconnectCallback(room.guest.uid);
            }
          }
        }
      }
    });

    // Listen for moves
    this.movesRef = ref(database, `moves/${this.currentRoom!.id}`);
    onValue(this.movesRef, (snapshot) => {
      if (snapshot.exists()) {
        const moves = snapshot.val();
        const moveKeys = Object.keys(moves).sort();
        const latestMove = moves[moveKeys[moveKeys.length - 1]] as GameMove;
        
        // Only process moves from the opponent
        if (latestMove.playerId !== this.currentUser?.uid) {
          const moveCallback = this.listeners.get('move');
          if (moveCallback) {
            moveCallback(latestMove);
          }
        }
      }
    });
  }

  public async leaveRoom(): Promise<void> {
    if (!this.currentRoom) return;

    this.updatePlayerPresence(false);

    // Remove listeners
    if (this.roomRef) {
      off(this.roomRef);
    }
    if (this.movesRef) {
      off(this.movesRef);
    }

    // If host leaves while waiting, delete the room
    if (this.isHost() && this.currentRoom.status === 'waiting') {
      await remove(ref(database, `rooms/${this.currentRoom.id}`));
    }

    this.currentRoom = null;
    this.roomRef = null;
    this.movesRef = null;
  }

  public isHost(): boolean {
    return this.currentRoom?.host.uid === this.currentUser?.uid;
  }

  public getCurrentRoom(): GameRoom | null {
    return this.currentRoom;
  }

  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  private serializeGameState(gameState: GameState): any {
    // Convert Map to object for Firebase
    const unitsObj: any = {};
    if (gameState.units instanceof Map) {
      gameState.units.forEach((unit, id) => {
        unitsObj[id] = unit;
      });
    }
    
    const resourcesObj: any = {};
    if (gameState.resources instanceof Map) {
      gameState.resources.forEach((value, faction) => {
        resourcesObj[faction] = value;
      });
    }
    
    const battleStatsObj: any = {};
    if (gameState.battleStats instanceof Map) {
      gameState.battleStats.forEach((stats, faction) => {
        battleStatsObj[faction] = stats;
      });
    }
    
    const serialized: any = {
      ...gameState,
      units: unitsObj,
      resources: resourcesObj,
      battleStats: battleStatsObj
    };

    // Remove any undefined values
    return JSON.parse(JSON.stringify(serialized));
  }

  public deserializeGameState(data: any): GameState {
    return {
      ...data,
      units: new Map(Object.entries(data.units || {})),
      resources: new Map(Object.entries(data.resources || {})),
      battleStats: data.battleStats ? new Map(Object.entries(data.battleStats)) : new Map()
    };
  }

  public async sendNotification(notification: NotificationData): Promise<void> {
    if (!this.currentRoom || !this.currentUser) {
      return; // Not in a room, skip
    }

    // Push notification to Firebase
    const notificationRef = ref(database, `rooms/${this.currentRoom.id}/notifications`);
    await push(notificationRef, notification);
  }

  public onNotification(callback: (notification: NotificationData) => void): void {
    if (!this.currentRoom) return;

    const notificationRef = ref(database, `rooms/${this.currentRoom.id}/notifications`);
    const listener = (snapshot: any) => {
      const data = snapshot.val();
      if (data) {
        // Process new notifications
        Object.values(data).forEach((notif: any) => {
          callback(notif);
        });
        // Clear processed notifications
        remove(notificationRef);
      }
    };

    onValue(notificationRef, listener);
    this.listeners.set('notifications', listener);
  }

  public cleanup() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.leaveRoom();
    this.listeners.clear();
  }
}